// Argus — PropertiesDialog: Windows-style properties pentru drive/folder/file (fix #10 iter2 round 2).
// Conținut:
//   Icoană | Nume | Type | Location | Size | Created at | Modified at | Last accessed at |
//   Attributes (Read-only, Archive, System, Hidden)
//
// Câmpurile created/lastAccessed/Read-only/Archive sunt OPTIONAL pe FsEntry (vor fi populate la
// extension agent dir_listing iter3). Pentru moment afișăm „—" dacă lipsesc.
//
// Faza 4 PoC: extensie pentru watch toggle (folder only) păstrată, dar afișată sub Apply doar
// dacă entry e folder (NU drive, NU file). Apply emit set_watched_paths.

import { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ArgusDialog } from '../../components/floating/ArgusDialog';
import { dayjs } from '../../lib/dayjsSetup';
import { formatBytes } from '../../lib/dirListing';
import { iconUrl, type FsEntry } from '../../lib/fsEntries';
import { iconCache } from '../../lib/iconCache';
import { fetchWatchedPaths, emitSetWatchedPaths, type WatchedPathInput, type FsEventType } from '../../lib/watchedPaths';

export interface PropertiesDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentOnline: boolean;
  folderName: string;
  fullPath: string;
  /** Entry-ul complet (file/folder/drive). Optional — folosit pentru detalii enriched. */
  entry?: FsEntry | null;
  initiallyWatched?: boolean;
  onApplied?: (watching: boolean) => void;
}

function formatTimestamp(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const m = dayjs(raw);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : '—';
}

function determineType(entry: FsEntry | null | undefined, fullPath: string): string {
  if (entry === null || entry === undefined) {
    if (fullPath === '') return 'This PC';
    if (/^[A-Za-z]:\\?$/.test(fullPath)) return 'Drive';
    return 'Folder';
  }
  if (entry.driveType !== null && entry.driveType !== undefined) {
    const map: Record<string, string> = {
      Local: 'Local Disk',
      Removable: 'Removable Disk',
      CDRom: 'CD/DVD Drive',
      Network: 'Network Drive',
      Fixed: 'Local Disk',
    };
    return map[entry.driveType] ?? `${entry.driveType} Drive`;
  }
  if (entry.isDir) return 'File folder';
  const idx = entry.name.lastIndexOf('.');
  if (idx > 0) return `${entry.name.substring(idx + 1).toUpperCase()} File`;
  return 'File';
}

function attrLabel(flag: boolean | undefined | null, labelOn: string): string | null {
  return flag === true ? labelOn : null;
}

export function PropertiesDialog({
  open, onClose, agentId, agentOnline, folderName, fullPath, entry, initiallyWatched = false, onApplied,
}: PropertiesDialogProps): JSX.Element {
  const [watch, setWatch] = useState<boolean>(initiallyWatched);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFolder = entry !== null && entry !== undefined && entry.isDir === true && (entry.driveType === null || entry.driveType === undefined);
  const isDrive = entry !== null && entry !== undefined && entry.driveType !== null && entry.driveType !== undefined;
  const isFile = entry !== null && entry !== undefined && entry.isDir === false;

  const type = determineType(entry, fullPath);
  const location = (() => {
    const idx = fullPath.lastIndexOf('\\');
    if (idx <= 0) return fullPath || 'This PC';
    return fullPath.substring(0, idx);
  })();
  const sizeText = (() => {
    if (entry === null || entry === undefined) return '—';
    if (isDrive) {
      const free = entry.freeBytes ?? 0;
      const total = entry.totalBytes ?? 0;
      if (total > 0) {
        return `${formatBytes(total - free)} used of ${formatBytes(total)} (${formatBytes(free)} free)`;
      }
      return '—';
    }
    if (isFolder) return '—'; // folder size requires recursive scan; deferred.
    if (entry.size !== null && entry.size !== undefined) return `${formatBytes(entry.size)} (${entry.size.toLocaleString()} bytes)`;
    return '—';
  })();

  // Extended timestamps: createdAt, lastAccessedAt — optional pe FsEntry (vor veni din agent iter3).
  const entryAny = entry as unknown as { createdAt?: string | null; lastAccessedAt?: string | null; isReadOnly?: boolean; isArchive?: boolean } | null | undefined;
  const createdAt = entryAny?.createdAt ?? null;
  const lastAccessedAt = entryAny?.lastAccessedAt ?? null;
  const modifiedAt = entry?.modifiedAt ?? null;

  const attributes: string[] = [];
  const ro = attrLabel(entryAny?.isReadOnly, 'Read-only'); if (ro !== null) attributes.push(ro);
  const ar = attrLabel(entryAny?.isArchive, 'Archive'); if (ar !== null) attributes.push(ar);
  const sy = attrLabel(entry?.isSystem, 'System'); if (sy !== null) attributes.push(sy);
  const hi = attrLabel(entry?.isHidden, 'Hidden'); if (hi !== null) attributes.push(hi);
  const attributesText = attributes.length > 0 ? attributes.join(', ') : '— (None)';

  const Icon = isDrive ? StorageIcon : isFile ? InsertDriveFileIcon : FolderIcon;
  const iconColor = isDrive ? 'primary' : isFile ? 'action' : 'warning';

  const handleApply = async () => {
    if (!agentOnline) return;
    setSubmitting(true);
    setError(null);
    try {
      // Bloc v1.0.10 #B fix: server `set_watched_paths` așteaptă bulk replace cu paths array
      // (NU single path/watch toggle). Fetch existing → modify → emit replace tranzacțional.
      const existing = await fetchWatchedPaths(agentId);
      const norm = (s: string) => s.replace(/\\+$/, '').toLowerCase();
      const target = norm(fullPath);
      const filtered = existing.items.filter((p) => norm(p.path) !== target);
      const next: WatchedPathInput[] = filtered.map((p) => ({
        path: p.path,
        recursive: p.recursive,
        eventTypes: p.eventTypes,
        extensionsWhitelist: p.extensionsWhitelist,
        extensionsBlacklist: p.extensionsBlacklist,
      }));
      if (watch) {
        next.push({
          path: fullPath,
          recursive: true,
          eventTypes: ['created', 'modified', 'deleted', 'renamed'] as FsEventType[],
          extensionsWhitelist: [],
          extensionsBlacklist: [],
        });
      }
      if (next.length === 0) {
        // Server validation requires min 1 path. Pentru "unwatch ultimul", admin folosește
        // pagina/modal Watched Paths cu DELETE explicit (out of scope here). Show clear error.
        setError('Cannot remove the last watched path through this dialog. Use Watched Paths admin window to clear all.');
        setSubmitting(false);
        return;
      }
      await emitSetWatchedPaths(agentId, next, watch ? 'Add watched path via Properties' : 'Remove watched path via Properties');
      onApplied?.(watch);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to emit set_watched_paths');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ArgusDialog
      open={open}
      onClose={onClose}
      title={`Properties — ${folderName}`}
      maxWidth="sm"
      minWidth={500}
      actions={
        isFolder ? (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={() => void handleApply()}
              variant="contained"
              disabled={submitting || !agentOnline}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? 'Applying…' : 'Apply'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained">Close</Button>
        )
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {(() => {
          const iconHash = entry?.iconHash ?? null;
          if (iconHash === null) {
            return <Icon sx={{ fontSize: 48, color: `${iconColor}.main` }} />;
          }
          const bundle = iconCache.getBundle(iconHash);
          const src = bundle ? bundle.url48 : `${iconUrl(agentId, iconHash)}?size=48`;
          return (
            <Box
              component="img"
              src={src}
              alt=""
              sx={{ width: 48, height: 48, objectFit: 'contain' }}
            />
          );
        })()}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{folderName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}>{fullPath || 'This PC'}</Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.75, columnGap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Type</Typography>
        <Typography variant="body2">{type}</Typography>

        <Typography variant="body2" color="text.secondary">Location</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{location}</Typography>

        <Typography variant="body2" color="text.secondary">Size</Typography>
        <Typography variant="body2">{sizeText}</Typography>

        <Typography variant="body2" color="text.secondary">Created at</Typography>
        <Typography variant="body2">{formatTimestamp(createdAt)}</Typography>

        <Typography variant="body2" color="text.secondary">Modified at</Typography>
        <Typography variant="body2">{formatTimestamp(modifiedAt)}</Typography>

        <Typography variant="body2" color="text.secondary">Last accessed at</Typography>
        <Typography variant="body2">{formatTimestamp(lastAccessedAt)}</Typography>

        <Typography variant="body2" color="text.secondary">Attributes</Typography>
        <Typography variant="body2">{attributesText}</Typography>
      </Box>

      {isFolder ? (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={watch}
                onChange={(e) => setWatch(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Watch this folder for changes"
          />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
            Notifies on file create / modify / delete. Real activation requires the agent watcher (deferred to continuous-monitoring-fs).
          </Typography>
        </>
      ) : null}

      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {!agentOnline && isFolder ? (
        <Alert severity="warning" sx={{ mt: 2 }}>Agent offline — changes cannot be applied now.</Alert>
      ) : null}
    </ArgusDialog>
  );
}
